import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../ui/use-toast';
import {
  Truck,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { AdminTransporterAPI } from '../../lib/api';
import {
  EnhancedDialog,
  EnhancedDialogContent,
  EnhancedDialogHeader,
  EnhancedDialogBody,
  EnhancedDialogFooter,
  EnhancedDialogTitle,
  EnhancedDialogDescription
} from '../ui/enhanced-dialog';
import { FormField, FormSection, FormGrid } from '../ui/form-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

interface VehicleCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  transporterId: string;
}

export default function VehicleCreateModal({
  isOpen,
  onClose,
  onSuccess,
  transporterId
}: VehicleCreateModalProps) {
  // Form state
  const [formData, setFormData] = useState({
    registrationNumber: '',
    type: 'truck',
    capacity: {
      value: '',
      unit: 'ton'
    },
    insuranceExpiryDate: '',
    isActive: true
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Vehicle types
  const vehicleTypes = [
    'truck', 'mini_truck', 'pickup', 'van', 'tempo', 'tractor', 'tipper', 'tanker', 'trailer', 'other'
  ];

  // Capacity units
  const capacityUnits = ['ton', 'kg', 'liter', 'cubic_meter', 'cubic_feet'];

  // Update form data
  const updateFormData = (field: string, value: any) => {
    if (field.startsWith('capacity.')) {
      const capacityField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        capacity: {
          ...prev.capacity,
          [capacityField]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.registrationNumber.trim()) newErrors.registrationNumber = 'Registration number is required';
    if (!formData.type) newErrors.type = 'Vehicle type is required';
    if (!formData.capacity.value) newErrors['capacity.value'] = 'Capacity value is required';
    if (!formData.capacity.unit) newErrors['capacity.unit'] = 'Capacity unit is required';

    // Registration number validation
    const regNoRegex = /^[A-Z]{2}[0-9]{1,2}[A-Z]{1,2}[0-9]{4}$/;
    if (formData.registrationNumber && !regNoRegex.test(formData.registrationNumber)) {
      newErrors.registrationNumber = 'Please enter a valid registration number (e.g., MH01AB1234)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      const response = await AdminTransporterAPI.addTransporterVehicle(transporterId, {
        ...formData,
        capacity: {
          ...formData.capacity,
          value: Number(formData.capacity.value)
        }
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "Vehicle added successfully",
          variant: "default"
        });

        handleClose();
        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to add vehicle",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error adding vehicle:', error);
      toast({
        title: "Error",
        description: "Failed to add vehicle. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      registrationNumber: '',
      type: 'truck',
      capacity: {
        value: '',
        unit: 'ton'
      },
      insuranceExpiryDate: '',
      isActive: true
    });
    setErrors({});
    onClose();
  };

  return (
    <EnhancedDialog open={isOpen} onOpenChange={handleClose}>
      <EnhancedDialogContent size="lg">
        <EnhancedDialogHeader>
          <EnhancedDialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Add New Vehicle
          </EnhancedDialogTitle>
          <EnhancedDialogDescription>
            Add a new vehicle to the transporter's fleet
          </EnhancedDialogDescription>
        </EnhancedDialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <EnhancedDialogBody>
            <FormSection
              title="Vehicle Details"
              icon={<Truck className="h-5 w-5" />}
              description="Basic vehicle information"
            >
              <FormField
                label="Registration Number"
                required
                error={errors.registrationNumber}
                hint="Format: MH01AB1234"
              >
                <Input
                  value={formData.registrationNumber}
                  onChange={(e) => updateFormData('registrationNumber', e.target.value.toUpperCase())}
                  placeholder="Enter registration number"
                />
              </FormField>

              <FormGrid columns={2}>
                <FormField
                  label="Vehicle Type"
                  required
                  error={errors.type}
                >
                  <Select
                    value={formData.type}
                    onValueChange={(value) => updateFormData('type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vehicle type" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField
                  label="Insurance Expiry Date"
                  hint="Optional"
                >
                  <Input
                    type="date"
                    value={formData.insuranceExpiryDate}
                    onChange={(e) => updateFormData('insuranceExpiryDate', e.target.value)}
                  />
                </FormField>
              </FormGrid>

              <FormGrid columns={2}>
                <FormField
                  label="Capacity Value"
                  required
                  error={errors['capacity.value']}
                >
                  <Input
                    type="number"
                    value={formData.capacity.value}
                    onChange={(e) => updateFormData('capacity.value', e.target.value)}
                    placeholder="Enter capacity value"
                  />
                </FormField>

                <FormField
                  label="Capacity Unit"
                  required
                  error={errors['capacity.unit']}
                >
                  <Select
                    value={formData.capacity.unit}
                    onValueChange={(value) => updateFormData('capacity.unit', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {capacityUnits.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit.charAt(0).toUpperCase() + unit.slice(1).replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              </FormGrid>
            </FormSection>
          </EnhancedDialogBody>

          <EnhancedDialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Add Vehicle
                </>
              )}
            </Button>
          </EnhancedDialogFooter>
        </form>
      </EnhancedDialogContent>
    </EnhancedDialog>
  );
}